import React from 'react';
import TextInput from '../../components/TextInput';
import WheelDatePicker from '../../components/WheelDatePicker';
import ChoicePicker from '../../components/ChoicePicker';
import { sendIcon } from '../../chatIcons';
import { normalizeMyNumber } from '../../GeneralProfile/useGeneralProfileFlow';
import type { DependentsSectionFlow } from './useDependentsSectionFlow';
import {
  DEPENDENT_CHANGE_REASON_CODES,
  isDependentChangeReasonCode,
  type DependentChangeReasonCode,
  type DependentNonResidentReason,
  type DependentRelationship,
  type DisabilityCategory,
  type YesNo,
} from './dependentsSectionTypes';

const YES_NO = (t: DependentsSectionFlow['t']): { value: YesNo; label: string }[] => [
  { value: 'yes', label: t('submitDocuments.dependentDeduction.spouse.yes') },
  { value: 'no', label: t('submitDocuments.dependentDeduction.spouse.no') },
];

const RELATIONSHIP_OPTIONS = (
  t: DependentsSectionFlow['t']
): { value: DependentRelationship; label: string }[] => [
  { value: 'child', label: t('submitDocuments.familyInfo.relationship.child') },
  { value: 'father', label: t('submitDocuments.familyInfo.relationship.father') },
  { value: 'mother', label: t('submitDocuments.familyInfo.relationship.mother') },
  { value: 'other', label: t('submitDocuments.dependentDeduction.dependents.relationship.other') },
];

const NON_RESIDENT_REASON_OPTIONS = (
  t: DependentsSectionFlow['t']
): { value: DependentNonResidentReason; label: string }[] => [
  {
    value: 'study_abroad',
    label: t('submitDocuments.dependentDeduction.dependents.nonResidentReason.option.study_abroad'),
  },
  {
    value: 'disability',
    label: t('submitDocuments.dependentDeduction.dependents.nonResidentReason.option.disability'),
  },
  {
    value: 'remittance_380k',
    label: t('submitDocuments.dependentDeduction.dependents.nonResidentReason.option.remittance_380k'),
  },
  {
    value: 'none',
    label: t('submitDocuments.dependentDeduction.dependents.nonResidentReason.option.none'),
  },
];

const DISABILITY_OPTIONS = (
  t: DependentsSectionFlow['t']
): { value: DisabilityCategory; label: string }[] => [
  { value: 'none', label: t('submitDocuments.profileName.disabilityCategory.option.none') },
  { value: 'general', label: t('submitDocuments.profileName.disabilityCategory.option.general') },
  { value: 'special', label: t('submitDocuments.profileName.disabilityCategory.option.special') },
];

const CHANGE_REASON_OPTIONS = (
  t: DependentsSectionFlow['t']
): { value: DependentChangeReasonCode; label: string }[] =>
  DEPENDENT_CHANGE_REASON_CODES.map((code) => ({
    value: code,
    label: t(`submitDocuments.dependentDeduction.dependents.changeReason.option.${code}`),
  }));

function HelpWrap({ flow, children }: { flow: DependentsSectionFlow; children: React.ReactNode }) {
  const help = flow.helpTextForStep(flow.step);
  return (
    <>
      {help ? (
        <div
          style={{
            padding: '10px 16px 0',
            fontSize: '0.9em',
            lineHeight: 1.55,
            color: 'var(--ff-text-muted, #555)',
          }}
        >
          {help}
        </div>
      ) : null}
      {children}
    </>
  );
}

function OkBar({ onOk, ariaLabel }: { onOk: () => void; ariaLabel: string }) {
  return (
    <div className="ff-text-input">
      <div className="ff-text-input__footer">
        <button type="button" className="ff-text-input__send" onClick={onOk} aria-label={ariaLabel}>
          <img src={sendIcon} alt="" aria-hidden />
        </button>
      </div>
    </div>
  );
}

export default function DependentsSectionChatComposer({
  flow,
  wheelLocale,
}: {
  flow: DependentsSectionFlow;
  wheelLocale?: string;
}) {
  const { step, draft, t } = flow;
  const sendAria = t('submitDocuments.profileName.send');

  if (step === 'finalConfirm') return null;

  if (step === 'hasDependents') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={flow.hasDependentsAnswer || null}
          options={YES_NO(t)}
          onChange={(v) => void flow.handleHasDependents(v)}
        />
      </div>
    );
  }

  if (step === 'addAnother') {
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={flow.addAnotherAnswer || null}
          options={YES_NO(t)}
          onChange={(v) => void flow.handleAddAnother(v)}
        />
      </div>
    );
  }

  if (step === 'ineligibleNotice') {
    return <OkBar onOk={() => void flow.handleIneligibleOk()} ariaLabel={sendAria} />;
  }

  if (step === 'ageCategoryNotice') {
    return <OkBar onOk={() => void flow.handleAgeCategoryOk()} ariaLabel={sendAria} />;
  }

  if (step === 'addName' || step === 'confirmFullName') {
    return (
      <TextInput
        fields={[
          {
            id: 'name',
            label: t('submitDocuments.dependentDeduction.dependents.fullNameLabel'),
            required: true,
            validate: flow.validateRequired,
          },
        ]}
        values={{ name: draft.fullName }}
        onChange={(_, v) => flow.setDraftField('fullName', v)}
        onSubmit={() => void flow.submitStep(step)}
        submitDisabled={!draft.fullName.trim()}
        submitAriaLabel={sendAria}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (step === 'addKana' || step === 'confirmFullNameKana') {
    return (
      <TextInput
        fields={[
          {
            id: 'kana',
            label: t('submitDocuments.dependentDeduction.dependents.fullNameKanaLabel'),
            required: true,
            validate: flow.validateKana,
          },
        ]}
        values={{ kana: draft.fullNameKana }}
        onChange={(_, v) => flow.setDraftField('fullNameKana', v)}
        onSubmit={() => void flow.submitStep(step)}
        submitDisabled={!draft.fullNameKana.trim()}
        submitAriaLabel={sendAria}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (step === 'addRelationship' || step === 'confirmRelationship') {
    const opts = RELATIONSHIP_OPTIONS(t);
    const current = opts.some((o) => o.value === draft.relationship)
      ? (draft.relationship as DependentRelationship)
      : null;
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={current}
          options={opts}
          onChange={(v) => void flow.submitStep(step, { relationship: v })}
        />
      </div>
    );
  }

  if (step === 'addRelationshipOther') {
    return (
      <TextInput
        fields={[
          {
            id: 'rel',
            label: t('submitDocuments.dependentDeduction.dependents.relationshipOtherLabel'),
            required: true,
            validate: flow.validateRequired,
          },
        ]}
        values={{ rel: draft.relationshipOther }}
        onChange={(_, v) => flow.setDraftField('relationshipOther', v)}
        onSubmit={() => void flow.submitStep(step)}
        submitDisabled={!draft.relationshipOther.trim()}
        submitAriaLabel={sendAria}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  if (step === 'addDob') {
    return (
      <div className="ff-text-input">
        <div className="ff-text-input__fields">
          <WheelDatePicker value={flow.dob} onChange={flow.setDob} locale={wheelLocale} />
        </div>
        <div className="ff-text-input__footer">
          <button
            type="button"
            className="ff-text-input__send"
            onClick={() => void flow.handleSendDob()}
            aria-label={sendAria}
          >
            <img src={sendIcon} alt="" aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  if (step === 'changeReason') {
    const selected = isDependentChangeReasonCode(draft.changeReason) ? draft.changeReason : null;
    return (
      <div style={{ padding: '12px 16px' }}>
        <ChoicePicker
          value={selected}
          options={CHANGE_REASON_OPTIONS(t)}
          onChange={(v) => void flow.submitStep('changeReason', { changeReason: v })}
        />
      </div>
    );
  }

  if (step === 'changeReasonOther') {
    return (
      <TextInput
        fields={[
          {
            id: 'reason',
            label: t('submitDocuments.dependentDeduction.dependents.changeReasonOtherLabel'),
            required: true,
            validate: flow.validateRequired,
          },
        ]}
        values={{ reason: draft.changeReason === 'other' ? '' : draft.changeReason }}
        onChange={(_, v) => flow.setDraftField('changeReason', v)}
        onSubmit={() => void flow.submitStep('changeReasonOther')}
        submitDisabled={!draft.changeReason.trim() || draft.changeReason === 'other'}
        submitAriaLabel={sendAria}
        requiredMessage={t('submitDocuments.profileName.validation.required')}
      />
    );
  }

  return null;
}
